import React, { Fragment, useCallback, useMemo, useState } from 'react';

import { groupBy, keys, sortBy } from 'lodash';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useGetBasicReports } from '@modules/reports/basicReports/duck/hooks';
import { NewCustomReportButton } from '@modules/reports/common/components/NewCustomReportButton/NewCustomReportButton';
import { Spin, T, Tooltip } from '@modules/shared/components';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { openBasicReport } from '@utils';

import { SingleReport } from '../../common';
import {
  byMemberIdReports,
  groupLevels,
  specialLabelGroups,
} from '../constants';
import { openSelectYouthModal } from '../duck';
import { NormalizedReportItem } from '../duck/utils';
import styles from './BasicReports.less';
import ByMemberIdModal from './ByMemberIdModal';
import SelectYouthModal from './SelectYouthModal';

const BasicReports = () => {
  const [showByMemberIdModal, setShowByMemberIdModal] = useState<{
    open: boolean;
    report: NormalizedReportItem | null;
  }>({ open: false, report: null });
  const dispatch = useDispatch();
  const organizationGuid = useSelector(organizationGuidSel);
  const { reports, loading } = useGetBasicReports();

  const onCloseByMemberIdModal = () =>
    setShowByMemberIdModal({ open: false, report: null });

  const handleClick = useCallback(
    report => () => {
      const { ReportURI, isIndividual, reportCode } = report;

      if (byMemberIdReports.includes(reportCode)) {
        return setShowByMemberIdModal({ open: true, report });
      }

      isIndividual
        ? dispatch(openSelectYouthModal(reportCode))
        : openBasicReport(ReportURI, { organizationGuid });
    },
    [dispatch, organizationGuid],
  );

  const groupedReports = useMemo(
    () => groupBy(reports, 'ReportGroupName'),
    [reports],
  );

  const getOpenLabel = (report: NormalizedReportItem) => {
    if (specialLabelGroups[report.ReportGroupName]) {
      return (
        <FormattedMessage
          id={`reports.BasicReport.${
            specialLabelGroups[report.ReportGroupName]
          }`}
        />
      );
    }

    return undefined;
  };

  const reportsList = useMemo(
    () =>
      sortBy(keys(groupedReports), [
        groupName => groupLevels[groupName] ?? 0,
        groupName => groupName.toLowerCase(),
      ]).map(reportGroupKey => (
        <div key={`groupedBasicReports_${reportGroupKey}`}>
          <div className={styles.title}>
            <T size="4" noMargin>
              {reportGroupKey}
            </T>
          </div>

          {groupedReports[reportGroupKey].map((report, index) => (
            <SingleReport
              key={report.id}
              name={report.ReportName}
              openLabel={getOpenLabel(report)}
              report={report}
              noDivider={index + 1 === groupedReports[reportGroupKey].length}
              onClick={handleClick(report)}
            />
          ))}
        </div>
      )),
    [groupedReports, handleClick],
  );

  return (
    <Fragment>
      <div className={styles.content}>
        <div className={styles.newReportButton}>
          <Tooltip
            title={
              <FormattedMessage id="reports.BasicReport.customReportTooltip" />
            }
          >
            <MdInfo className={styles.infoIcon} />
          </Tooltip>
          <NewCustomReportButton />
        </div>
        <SelectYouthModal />
        <div className={styles.reportsList}>
          <Spin spinning={loading}>{reportsList}</Spin>
        </div>
      </div>
      <ByMemberIdModal
        isVisible={showByMemberIdModal.open}
        report={showByMemberIdModal.report}
        onClose={onCloseByMemberIdModal}
      />
    </Fragment>
  );
};

export default BasicReports;
